import type { Ref } from 'vue';
import { onBeforeUnmount, ref, watch } from 'vue';
import debounce from 'debounce';
import elementResizeDetectorMaker from 'element-resize-detector';

export const useDimensionsUpdating = (element: Ref<HTMLElement>) => {
  const elementResizeDetector = elementResizeDetectorMaker();

  const dimensionsUpdating = ref(false);
  let initialWidth = 0;
  let initialHeight = 0;
  let elementChangesTracked = false;

  const isUnchanged = (e: HTMLElement) =>
    e.offsetWidth === initialWidth && e.offsetHeight === initialHeight;

  const debounceResetDimensionsUpdating = debounce(() => (dimensionsUpdating.value = false), 300);

  const setNewDimensions = (e: HTMLElement) => {
    initialWidth = e.offsetWidth;
    initialHeight = e.offsetHeight;
    dimensionsUpdating.value = true;
    debounceResetDimensionsUpdating();
  };

  const addListener = () =>
    elementResizeDetector.listenTo(element.value, (e: HTMLElement) => {
      if (isUnchanged(e)) {
        return;
      }
      setNewDimensions(e);
    });

  watch(
    element,
    () => {
      if (!element.value || elementChangesTracked) {
        return;
      }
      elementChangesTracked = true;
      addListener();
    },
    { immediate: true }
  );

  watch(element, (newContainer, oldContainer) => {
    if (newContainer && !oldContainer) {
      addListener();
    }
  });

  onBeforeUnmount(() => {
    initialHeight = 0;
    initialWidth = 0;

    if (!element.value) {
      return;
    }
    elementResizeDetector.removeListener(element.value);
  });

  return { dimensionsUpdating, addListener, setNewDimensions };
};
